/* eslint-disable no-underscore-dangle -- Customer Labs SDK */
import type {
  CLCustomProperties,
  CLPropertyValue,
  CLProductProperties,
  ClProductLine,
  ClCommonDataLayer,
  ClEventName,
  ClEventPayload,
} from 'common/interfaces/customerLabs.interface';
import type { IUser } from '@/common/interfaces/user.interface';
import { CL_DATA_KEYS, CLICK_ID_TO_CL_DATA_KEY } from '@/constants/CustomerLabs';

const CL_STRING_TRAIT_KEYS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'school_year',
  'organization',
  'school',
  'position',
  'title',
  'members_in_organization',
] as const;

type CLStringTraitKey = (typeof CL_STRING_TRAIT_KEYS)[number];

/** Traits passed to Customer Labs identify; subset of IUser keyed by CL_STRING_TRAIT_KEYS. */
export type CLTraits = Partial<Pick<IUser, CLStringTraitKey>>;

function normalizePhoneForCustomerLabs(raw: string | undefined | null): string | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const trimmed = raw.trim();
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return undefined;
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11) return `+${digits}`;
  return digits;
}

function trimObject<T extends object>(obj: T): Partial<T> | null {
  const entries = Object.entries(obj).flatMap(([key, value]): [string, unknown][] => {
    if (value == null) return [];
    if (typeof value === 'string') {
      const t = value.trim();
      return t === '' ? [] : [[key, t]];
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      const nested = trimObject(value as object);
      return nested === null ? [] : [[key, nested]];
    }
    return [[key, value]];
  });

  return entries.length === 0 ? null : (Object.fromEntries(entries) as Partial<T>);
}

type ClickIdDataLayer = Partial<
  Record<(typeof CLICK_ID_TO_CL_DATA_KEY)[keyof typeof CLICK_ID_TO_CL_DATA_KEY], string>
>;

const getClickIdDataLayer = (utmParams: URLSearchParams | null): ClickIdDataLayer =>
  Object.entries(CLICK_ID_TO_CL_DATA_KEY).reduce<ClickIdDataLayer>((acc, [queryParam, clKey]) => {
    const value = utmParams?.get(queryParam);
    if (value) {
      acc[clKey] = value;
    }

    return acc;
  }, {});

export const buildClDataLayer = (
  action: ClEventName,
  user: IUser | null | undefined,
  utmParams: URLSearchParams | null
): ClCommonDataLayer => {
  const clickIdData = getClickIdDataLayer(utmParams);
  const base: ClCommonDataLayer = {
    [CL_DATA_KEYS.ACTION_NAME]: action,
    [CL_DATA_KEYS.ACTION_TIMESTAMP]: new Date().toISOString(),
    ...clickIdData,
  };

  if (!user) {
    return base;
  }

  const phoneForCl = normalizePhoneForCustomerLabs(user.phone);

  return {
    ...base,
    [CL_DATA_KEYS.EMAIL]: user.email,
    [CL_DATA_KEYS.PHONE_NUMBER]: phoneForCl ?? undefined,
    [CL_DATA_KEYS.FIRST_NAME]: user.first_name,
    [CL_DATA_KEYS.LAST_NAME]: user.last_name,
    [CL_DATA_KEYS.ORG]: user.organization ?? undefined,
    [CL_DATA_KEYS.SCHOOL]: user.school,
    [CL_DATA_KEYS.ROLE]: user.position ?? undefined,
    [CL_DATA_KEYS.MEMBERS_IN_ORG]: user.members_in_organization ?? undefined,
    [CL_DATA_KEYS.GRAD_YEAR]: user.school_year?.toString(),
  };
};

const toClProperty = (value: string | number | boolean | null | undefined): CLPropertyValue => ({
  t: typeof value === 'number' ? 'number' : 'string',
  v: typeof value === 'number' ? value : String(value ?? ''),
});

const toClCustomProperties = (payload: ClEventPayload): CLCustomProperties =>
  Object.entries(payload).reduce<CLCustomProperties>((acc, [key, value]) => {
    if (value != null) {
      acc[key] = toClProperty(value);
    }

    return acc;
  }, {});

const addOptionalClProductField = (
  row: CLProductProperties,
  key: string,
  value: string | number | null | undefined
) => {
  if (typeof value === 'number' && !Number.isFinite(value)) return;
  if (value == null || String(value).trim() === '') return;
  row[key] = toClProperty(value);
};

export const buildClProductProperties = (lines: ClProductLine[]): CLProductProperties[] =>
  lines.reduce<CLProductProperties[]>((acc, line) => {
    const hasName = line.name != null && String(line.name).trim() !== '';
    if (!hasName) return acc;

    const row: CLProductProperties = {};

    addOptionalClProductField(row, 'product_name', line.name);
    addOptionalClProductField(row, 'product_id', line.styleCode);
    addOptionalClProductField(row, 'product_brand', line.brandName);
    addOptionalClProductField(row, 'product_variant', line.colorName);
    addOptionalClProductField(row, 'product_image', line.imageUrl);
    addOptionalClProductField(row, 'product_price', line.price);
    addOptionalClProductField(row, 'product_quantity', line.quantity);

    acc.push(row);
    return acc;
  }, []);

export const sendClEvent = (
  eventName: ClEventName,
  payload: ClEventPayload,
  productProperties?: CLProductProperties[]
) => {
  /* eslint-disable no-underscore-dangle -- CustomerLabs injects global `_cl` */
  try {
    if (!window._cl) return;

    window._cl.track(eventName, {
      customProperties: toClCustomProperties(payload),
      ...(productProperties?.length ? { productProperties } : {}),
    });
  } catch (e) {
    console.error('[CL Tracking Error]', e);
  }
  /* eslint-enable no-underscore-dangle -- CustomerLabs injects global `_cl` */
};

function toClPropForIdentify(value: unknown): CLPropertyValue {
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && value !== '' && /^\d+$/.test(value))
  ) {
    const n = typeof value === 'number' ? value : Number(value);
    return { t: 'number', v: n };
  }
  return { t: 'string', v: value != null ? String(value) : '' };
}

function buildClIdentifyUserTraits(userInput: CLTraits): Record<string, CLPropertyValue> {
  const userTraits = CL_STRING_TRAIT_KEYS.reduce<Record<string, CLPropertyValue>>((acc, key) => {
    if (key === 'phone') return acc;
    const v = userInput[key];
    if (v != null) acc[key] = toClPropForIdentify(v);
    return acc;
  }, {});

  const normalizedPhone = normalizePhoneForCustomerLabs(userInput.phone);
  if (normalizedPhone !== undefined) {
    userTraits.phone = { t: 'string', v: normalizedPhone };
  }

  if (userInput.school_year != null) {
    userTraits.school_year = toClPropForIdentify(String(userInput.school_year));
  }

  return userTraits;
}

/**
 * Stable trait subset for `useMemo`; `null` when identify should not run (no email).
 */
export function getClIdentifyDedupeTraits(userInfo: IUser): CLTraits | null {
  if (!userInfo.email?.trim()) return null;

  const traits: Pick<IUser, CLStringTraitKey> = {
    first_name: userInfo.first_name,
    last_name: userInfo.last_name,
    email: userInfo.email,
    phone: userInfo.phone,
    school_year: userInfo.school_year,
    organization: userInfo.organization,
    school: userInfo.school,
    position: userInfo.position,
    title: userInfo.title,
    members_in_organization: userInfo.members_in_organization ?? undefined,
  };

  return trimObject(traits);
}

function clIdentifyUser(userInput: CLTraits, isUpdate = false): void {
  /* eslint-disable no-underscore-dangle -- CustomerLabs injects global `_cl` */
  if (typeof window === 'undefined' || !window._cl || typeof window._cl.identify !== 'function') {
    return;
  }

  const { email } = userInput;
  if (!email) return;

  const userTraits = buildClIdentifyUserTraits(userInput);
  const customProperties = {
    user_traits: { t: 'Object' as const, v: userTraits },
    identify_by_email: { t: 'string' as const, v: email, ib: true },
  };

  if (isUpdate) window._cl.identify({ customProperties }, 'Update user');
  else window._cl.identify({ customProperties });
  /* eslint-enable no-underscore-dangle -- CustomerLabs injects global `_cl` */
}

export function clIdentifyAndUpdateUser(userInfo: CLTraits): () => void {
  clIdentifyUser(userInfo);

  const timeoutId = window.setTimeout(() => {
    clIdentifyUser(userInfo, true);
  }, 2500);

  return () => {
    window.clearTimeout(timeoutId);
  };
}
